import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Drawer, Space, Spin, Tag, Typography } from 'antd';
import api from '../api/client';

const { Text } = Typography;

const ROLE_COLORS: Record<string, string> = {
  employee: 'blue',
  client: 'green',
  counterparty_employee: 'orange',
};

interface IndData {
  id: number;
  last_name: string;
  first_name: string;
  middle_name?: string;
  gender?: string;
  job_title?: string;
  notes?: string;
  is_active: boolean;
  phones: string[];
  emails: string[];
  roles: { role: string; label: string; counterparty_id: number | null; counterparty_name: string | null }[];
  tab_number?: string;
  hire_date?: string;
  position?: { id: number; name: string };
  department?: { id: number; name: string };
  organization?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  individualId: number | null;
  extra?: React.ReactNode;
  onCounterpartyClick?: (id: number) => void;
  refreshKey?: number;
}

const IndividualInfoDrawer: React.FC<Props> = ({
  open, onClose, individualId, extra, onCounterpartyClick, refreshKey,
}) => {
  const [data, setData] = useState<IndData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !individualId) return;
    setLoading(true);
    setData(null);
    api.get(`/individuals/${individualId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, individualId, refreshKey]);

  const fullName = data
    ? [data.last_name, data.first_name, data.middle_name].filter(Boolean).join(' ')
    : 'Физлицо';

  return (
    <Drawer
      title={fullName}
      open={open}
      onClose={onClose}
      width={480}
      extra={extra}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : data && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            {data.is_active
              ? <Tag color="green">Активен</Tag>
              : <Tag color="default">Архив</Tag>}
            {data.roles.map(r => (
              <Tag key={r.role} color={ROLE_COLORS[r.role]}>{r.label}</Tag>
            ))}
          </Space>

          <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
            {data.gender && (
              <Descriptions.Item label="Пол">
                {data.gender === 'М' ? 'Мужской' : 'Женский'}
              </Descriptions.Item>
            )}
            {data.notes && (
              <Descriptions.Item label="Заметки">{data.notes}</Descriptions.Item>
            )}
          </Descriptions>

          {data.phones.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong>Телефон</Text>
              {data.phones.map((p, i) => (
                <div key={i} style={{ color: '#555' }}>{p}</div>
              ))}
            </div>
          )}

          {data.emails.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text strong>Email</Text>
              {data.emails.map((e, i) => (
                <div key={i}><a href={`mailto:${e}`}>{e}</a></div>
              ))}
            </div>
          )}

          {/* Как сотрудник */}
          {data.roles.some(r => r.role === 'employee') && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Как сотрудник</Text>
              <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                {data.tab_number && (
                  <Descriptions.Item label="Таб. номер">{data.tab_number}</Descriptions.Item>
                )}
                {data.department && (
                  <Descriptions.Item label="Подразделение">{data.department.name}</Descriptions.Item>
                )}
                {data.position && (
                  <Descriptions.Item label="Должность">{data.position.name}</Descriptions.Item>
                )}
                {data.organization && (
                  <Descriptions.Item label="Организация">{data.organization}</Descriptions.Item>
                )}
                {data.hire_date && (
                  <Descriptions.Item label="Дата найма">{data.hire_date}</Descriptions.Item>
                )}
              </Descriptions>
              <Button
                type="link"
                style={{ paddingLeft: 0, marginTop: 4 }}
                onClick={() => window.open(`/employees/${data.id}/edit`, '_blank')}
              >
                Открыть карточку сотрудника →
              </Button>
            </div>
          )}

          {/* Работа */}
          {(data.job_title || data.roles.some(r => r.role === 'counterparty_employee')) && (
            <div style={{ marginTop: 12, marginBottom: 8 }}>
              <Text strong>Работа</Text>
              <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                {data.job_title && (
                  <Descriptions.Item label="Должность">{data.job_title}</Descriptions.Item>
                )}
                {data.roles.filter(r => r.role === 'counterparty_employee').map((r, i) => (
                  <Descriptions.Item key={i} label="Организация">
                    {r.counterparty_id && onCounterpartyClick ? (
                      <Button
                        type="link"
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => onCounterpartyClick(r.counterparty_id!)}
                      >
                        {r.counterparty_name || `#${r.counterparty_id}`}
                      </Button>
                    ) : (r.counterparty_name || (r.counterparty_id ? `#${r.counterparty_id}` : '—'))}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default IndividualInfoDrawer;
